import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Tooltip,
  Tree,
  message,
} from "antd";
import { Prohibit } from "@phosphor-icons/react";

import {
  createPortalShare,
  fetchPortalShareOptions,
  revokePortalShare,
} from "../../service/portalShare";
import {
  indexFolders,
  summariseSend,
  toTreeNodes,
} from "./shareTree";
import {
  ActiveShare,
  ActiveShares,
  Count,
  EmptyNote,
  FolderRow,
  InviteLink,
  Panel,
  PanelLabel,
  Row,
  SectionTitle,
  ShareMeta,
  Summary,
  TreeWrapper,
} from "./ShareWithExpertModalStyled";

const NEW_PERSON = "__new__";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Sending a client's folders to an outside expert.
 *
 * The screen is built around the one number that matters — how many
 * documents this actually sends — because the mistake worth engineering
 * against is not a failed request, it is a share that contains one
 * document more than the person who made it believed it did.
 */
const ShareWithExpertModal = ({ isOpen, onClose, matterId, clientName }) => {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [invitationLink, setInvitationLink] = useState("");
  const [copyFailed, setCopyFailed] = useState(false);

  const [checkedKeys, setCheckedKeys] = useState([]);
  const [includeUnfiled, setIncludeUnfiled] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [readerId, setReaderId] = useState(NEW_PERSON);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(30);

  // `quiet` re-reads without raising the spinner. Refreshing after a
  // share must not blank the body, because the set-up link the paralegal
  // needs to copy lives there — hiding it behind a spinner defeats the
  // reason it is on screen at all.
  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      setError("");
      try {
        const response = await fetchPortalShareOptions(matterId);
        setOptions(response.data);
        const firms = response.data?.organizations || [];
        if (firms.length === 1) setOrganizationId(firms[0].id);
      } catch (err) {
        setError(
          err?.response?.data?.detail ||
            "Could not load this file's folders. Try again."
        );
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [matterId]
  );

  useEffect(() => {
    if (!isOpen) return;
    setInvitationLink("");
    setCopyFailed(false);
    setCheckedKeys([]);
    setIncludeUnfiled(false);
    load();
  }, [isOpen, load]);

  const index = useMemo(
    () => indexFolders(options?.folders || []),
    [options]
  );
  const treeData = useMemo(
    () => toTreeNodes(options?.folders || []),
    [options]
  );
  const { folderIds, sending, heldBack } = useMemo(
    () =>
      summariseSend({
        checkedKeys,
        index,
        includeUnfiled,
        unfiledCount: options?.unfiled_document_count || 0,
      }),
    [checkedKeys, index, includeUnfiled, options]
  );

  const firms = options?.organizations || [];
  const readers =
    firms.find((firm) => firm.id === organizationId)?.readers || [];
  const nothingChosen = folderIds.length === 0 && !includeUnfiled;

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await createPortalShare(matterId, {
        organization_id: organizationId,
        ...(readerId === NEW_PERSON
          ? { name: name.trim(), email: email.trim() }
          : { reader_id: readerId }),
        folder_ids: folderIds,
        include_unfiled: includeUnfiled,
        days,
      });
      // A set-up link only comes back for someone who has never signed
      // in. Keeping the modal open for it beats a snackbar that vanishes
      // while they are finding the doctor's email.
      if (response.data?.invitation_link) {
        setInvitationLink(response.data.invitation_link);
      } else {
        message.success(
          `Shared with ${response.data?.recipient?.name}. They can already sign in.`
        );
      }
      setName("");
      setEmail("");
      await load({ quiet: true });
    } catch (err) {
      // The backend answers refusals in a sentence a paralegal can act
      // on, so show it rather than replacing it with something vaguer.
      setError(
        err?.response?.data?.detail ||
          "Could not share this file. Nothing was sent."
      );
    } finally {
      setSaving(false);
    }
  };

  // The clipboard can refuse — no permission, an insecure origin, an
  // older browser. Claiming success anyway is worse than saying nothing:
  // the paralegal pastes whatever was on the clipboard before into an
  // email to a doctor, and the link they meant to send is gone as soon as
  // this panel closes. The link stays selectable in the field either way.
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopyFailed(false);
      message.success("Link copied.");
    } catch {
      // Said in the panel rather than a toast, because that is where the
      // paralegal is looking and a toast is dismissable by not noticing.
      setCopyFailed(true);
    }
  };

  const revoke = async (share) => {
    try {
      await revokePortalShare(matterId, share.grant_id);
      message.success(`${share.recipient.name} can no longer open this file.`);
      await load({ quiet: true });
    } catch (err) {
      message.error(
        err?.response?.data?.detail || "Could not stop that share."
      );
    }
  };

  const renderTitle = (node) => (
    <FolderRow $muted={!node.shareable}>
      <span>{node.title}</span>
      {node.shareable ? (
        <Count>{node.documentCount}</Count>
      ) : (
        <Tooltip title={node.reason}>
          <Count $muted>
            <Prohibit size={13} /> work product
          </Count>
        </Tooltip>
      )}
    </FolderRow>
  );

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title={`Share with an outside expert${clientName ? ` — ${clientName}` : ""}`}
      width={720}
      footer={null}
      destroyOnHidden
    >
      {loading && <Spin />}

      {!loading && options && (
        <>
          {invitationLink ? (
            <Panel>
              <PanelLabel>Send this link to the expert</PanelLabel>
              <p>
                It sets up their sign-in and works once. Everything after
                that is their own passkey — there is no password to send.
              </p>
              <InviteLink>
                <Input readOnly value={invitationLink} />
                <Button onClick={copyLink}>Copy</Button>
              </InviteLink>
              {copyFailed && (
                <EmptyNote>
                  Could not reach the clipboard — select the link above and
                  copy it by hand.
                </EmptyNote>
              )}
              <Button type="primary" onClick={() => setInvitationLink("")}>
                Done
              </Button>
            </Panel>
          ) : (
            <>
              <SectionTitle>Which folders go?</SectionTitle>
              {treeData.length === 0 ? (
                <EmptyNote>
                  This file has no folders yet, so there is nothing to tick.
                </EmptyNote>
              ) : (
                <TreeWrapper>
                  <Tree
                    checkable
                    selectable={false}
                    defaultExpandAll
                    treeData={treeData}
                    checkedKeys={checkedKeys}
                    onCheck={(keys) => setCheckedKeys(keys)}
                    titleRender={renderTitle}
                  />
                </TreeWrapper>
              )}

              {/* Documents filed straight on the matter belong to no
                  folder, so no tick can reach them. They are their own
                  choice, and off unless asked for. */}
              {options.unfiled_document_count > 0 && (
                <Row>
                  <Checkbox
                    checked={includeUnfiled}
                    onChange={(event) =>
                      setIncludeUnfiled(event.target.checked)
                    }
                  >
                    Also send the{" "}
                    {plural(options.unfiled_document_count, "document")} filed
                    straight on the file, in no folder
                  </Checkbox>
                </Row>
              )}

              <Summary $none={nothingChosen}>
                {nothingChosen
                  ? "Nothing ticked yet."
                  : `This sends ${plural(sending, "document")}.`}
                {heldBack > 0 && (
                  <em>
                    {" "}
                    {plural(heldBack, "document")} inside what you ticked
                    {" "}
                    {heldBack === 1 ? "is" : "are"} the firm&apos;s own work
                    product and {heldBack === 1 ? "stays" : "stay"} here.
                  </em>
                )}
              </Summary>

              <SectionTitle>Who reads it?</SectionTitle>
              <Row>
                <Select
                  aria-label="Which firm?"
                  placeholder="Which firm?"
                  style={{ width: 260 }}
                  value={organizationId}
                  onChange={(value) => {
                    setOrganizationId(value);
                    setReaderId(NEW_PERSON);
                  }}
                  options={firms.map((firm) => ({
                    value: firm.id,
                    label: firm.name,
                  }))}
                  notFoundContent="No outside firm is cleared to receive files yet."
                />
                {organizationId && (
                  <Select
                    aria-label="Which person?"
                    style={{ width: 260 }}
                    value={readerId}
                    onChange={setReaderId}
                    options={[
                      ...readers.map((reader) => ({
                        value: reader.id,
                        label: `${reader.name} — ${reader.email}`,
                      })),
                      { value: NEW_PERSON, label: "Someone new…" },
                    ]}
                  />
                )}
              </Row>

              {organizationId && readerId === NEW_PERSON && (
                <Row>
                  <Input
                    placeholder="Their name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    style={{ width: 260 }}
                  />
                  <Input
                    placeholder="Their work email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    style={{ width: 260 }}
                  />
                </Row>
              )}

              <Row>
                <span>Access ends after</span>
                <InputNumber
                  min={1}
                  max={180}
                  value={days}
                  onChange={(value) => setDays(value || 30)}
                />
                <span>days.</span>
              </Row>

              {error && <Alert type="error" message={error} showIcon />}

              <Row $right>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                  type="primary"
                  loading={saving}
                  disabled={
                    nothingChosen ||
                    !organizationId ||
                    (readerId === NEW_PERSON && (!name.trim() || !email.trim()))
                  }
                  onClick={submit}
                >
                  Share
                </Button>
              </Row>
            </>
          )}

          {/* Matthew chose that a ticked folder keeps sending, so anything
              filed there later goes too. That is only safe if staff can
              see, from the file, what is currently out. */}
          <SectionTitle>Currently shared</SectionTitle>
          {options.shares.length === 0 ? (
            <EmptyNote>
              Nobody outside the firm can open this file.
            </EmptyNote>
          ) : (
            <ActiveShares>
              {options.shares.map((share) => (
                <ActiveShare key={share.grant_id}>
                  <div>
                    <strong>{share.recipient.name}</strong>
                    <ShareMeta>
                      {share.recipient.organization} ·{" "}
                      {share.folders.length > 0
                        ? share.folders.map((f) => f.name).join(", ")
                        : "no folders"}
                      {share.includes_unfiled_documents && ", loose documents"}
                      {" · "}
                      {plural(share.document_count, "document")}
                      {!share.has_signed_in && " · has not signed in yet"}
                    </ShareMeta>
                  </div>
                  <Button danger size="small" onClick={() => revoke(share)}>
                    Stop sharing
                  </Button>
                </ActiveShare>
              ))}
            </ActiveShares>
          )}
        </>
      )}

      {!loading && !options && error && (
        <Alert type="error" message={error} showIcon />
      )}
    </Modal>
  );
};

ShareWithExpertModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  matterId: PropTypes.string.isRequired,
  clientName: PropTypes.string,
};

export default ShareWithExpertModal;
